import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from '../axiosConfig';
import { typeIcons } from './Utils';
import './AccountSelector.css'; // Reuse the same CSS as AccountSelector

// Up to this many possible contacts are all listed as soon as the box is
// focused; with more, the list asks for a search (1+ characters) first.
const LIST_ALL_MAX_OPTIONS = 10;
const MAX_SEARCH_RESULTS = 5;

const contactName = (contact) => `${contact.first_name || ''} ${contact.last_name || ''}`.trim();

// Props:
//   value      – the selected contact (object or id)
//   onChange   – (contact | null) => void
//   accountId  – only offer contacts of this account
//   accountIds – only offer contacts of any of these accounts (overrides accountId)
const ContactSelector = ({ value, onChange, accountId, accountIds }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [allContacts, setAllContacts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [dropdownRect, setDropdownRect] = useState(null);
  const inputWrapperRef = useRef(null);

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const res = await axios.get('/contacts/', { withCredentials: true });
        const contacts = res.data;
        setAllContacts(contacts);

        if (value) {
          const found = contacts.find(c => c.id === (value.id || value));
          setSelectedContact(found || null);
          if (found) setQuery(contactName(found));
        }
      } catch (err) {
        console.error('Error fetching contacts:', err);
      } finally {
        setLoaded(true);
      }
    };

    fetchContacts();
  }, [value]);

  const filterIds = accountIds
    ? accountIds.filter(Boolean)
    : (accountId ? [accountId] : null);
  const pool = filterIds
    ? allContacts.filter(contact => filterIds.includes(contact.account_id))
    : allContacts;
  const listAll = pool.length <= LIST_ALL_MAX_OPTIONS;

  const queryLower = query.trim().toLowerCase();
  const matches = !queryLower
    ? (listAll ? pool : [])
    : pool.filter(contact => (
        contactName(contact).toLowerCase().includes(queryLower) ||
        contact.email?.toLowerCase().includes(queryLower) ||
        contact.job_title?.toLowerCase().includes(queryLower)
      )).slice(0, listAll ? undefined : MAX_SEARCH_RESULTS);

  // What the list says when it has no contacts to offer.
  let hint = null;
  if (!loaded) hint = 'Loading contacts...';
  else if (pool.length === 0) hint = 'No contacts available';
  else if (!queryLower && !listAll) hint = 'Search to find contact';
  else if (matches.length === 0) hint = 'No matching contacts';

  const showList = open && !selectedContact;

  // Position the dropdown relative to the viewport (rather than the nearest
  // scrollable/clipping ancestor) so it can render via a portal on top of
  // everything, including modals with overflow:hidden/auto.
  useEffect(() => {
    if (!showList) {
      setDropdownRect(null);
      return;
    }

    const updateRect = () => {
      if (!inputWrapperRef.current) return;
      const rect = inputWrapperRef.current.getBoundingClientRect();
      setDropdownRect({
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      });
    };

    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [showList]);

  const handleSelect = (contact) => {
    onChange(contact);
    setSelectedContact(contact);
    setQuery(contactName(contact));
    setOpen(false);
  };

  const clearSelection = () => {
    onChange(null);
    setSelectedContact(null);
    setQuery('');
  };

  return (
    <div className="account-selector">
      <div className="input-wrapper" ref={inputWrapperRef}>
        <input autoComplete="off"
          id="contact-search"
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
          placeholder={listAll ? 'Select a contact...' : 'Type a contact name...'}
          readOnly={!!selectedContact}
        />
        {selectedContact && (
          <button
            type="button"
            className="remove-account-btn"
            onClick={clearSelection}
          >
            ×
          </button>
        )}
      </div>

      {showList && dropdownRect && createPortal(
        // Mousedown in the list mustn't take focus from the input (its blur
        // closes the list), so contacts are picked on mousedown too.
        <ul
          className="account-matches"
          style={{
            top: dropdownRect.top,
            left: dropdownRect.left,
            width: dropdownRect.width,
          }}
          onMouseDown={(e) => e.preventDefault()}
        >
          {hint ? (
            <li className="account-match-hint">{hint}</li>
          ) : matches.map((contact) => (
            <li
              key={contact.id}
              onMouseDown={() => handleSelect(contact)}
              className="account-match"
            >
              {/* Laid out like the header search's contact results */}
              {typeIcons.contact}
              <strong> {contactName(contact)}</strong>
              {(contact.job_title || contact.name_short) && (
                <span>
                  {' – '}
                  {[contact.job_title, contact.name_short].filter(Boolean).join(', ')}
                </span>
              )}
            </li>
          ))}
        </ul>,
        document.body
      )}
    </div>
  );
};

export default ContactSelector;
